/* ================================================================
   GORILLA MOUNTS — Shared scripts
   Mobile nav, scroll-reveal, carousels, FAQ, cart UI, gallery
   ================================================================ */
(function () {
  'use strict';

  // ---------------------------------------------------------------
  // Mobile nav toggle
  // ---------------------------------------------------------------
  const navToggle = document.querySelector('.nav-toggle');
  const navLinks = document.querySelector('.nav-links');
  if (navToggle && navLinks) {
    navToggle.addEventListener('click', () => {
      const open = navLinks.classList.toggle('open');
      navToggle.classList.toggle('open', open);
      navToggle.setAttribute('aria-expanded', String(open));
    });

    // Close on link click (mobile)
    navLinks.querySelectorAll('a').forEach(a => {
      a.addEventListener('click', () => {
        if (window.innerWidth <= 960) {
          navLinks.classList.remove('open');
          navToggle.classList.remove('open');
        }
      });
    });
  }

  // ---------------------------------------------------------------
  // Sticky-nav shadow on scroll
  // ---------------------------------------------------------------
  const nav = document.querySelector('.site-nav');
  if (nav) {
    const onScroll = () => {
      nav.classList.toggle('scrolled', window.scrollY > 8);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  // ---------------------------------------------------------------
  // Scroll-reveal (IntersectionObserver)
  // ---------------------------------------------------------------
  const reveals = document.querySelectorAll('.reveal');
  if ('IntersectionObserver' in window && reveals.length) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('in-view');
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
    reveals.forEach((el) => io.observe(el));
  } else {
    reveals.forEach((el) => el.classList.add('in-view'));
  }

  // ---------------------------------------------------------------
  // Video poster: load YouTube only after click
  // ---------------------------------------------------------------
  document.querySelectorAll('[data-youtube-src]').forEach((poster) => {
    poster.addEventListener('click', () => {
      const src = poster.dataset.youtubeSrc;
      if (!src) return;
      const title = poster.dataset.videoTitle || 'Video';
      const separator = src.includes('?') ? '&' : '?';
      const iframe = document.createElement('iframe');
      iframe.src = `${src}${separator}autoplay=1&rel=0`;
      iframe.title = title;
      iframe.loading = 'lazy';
      iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
      iframe.referrerPolicy = 'strict-origin-when-cross-origin';
      iframe.allowFullscreen = true;
      poster.replaceWith(iframe);
    });
  });

  // ---------------------------------------------------------------
  // FAQ accordion
  // ---------------------------------------------------------------
  document.querySelectorAll('.faq-item').forEach((item) => {
    const q = item.querySelector('.faq-q');
    if (!q) return;
    q.addEventListener('click', () => {
      const isOpen = item.classList.contains('open');
      // Optional: close siblings
      const siblings = item.parentElement?.querySelectorAll('.faq-item.open') || [];
      siblings.forEach((s) => { if (s !== item) s.classList.remove('open'); });
      item.classList.toggle('open', !isOpen);
    });
  });

  // ---------------------------------------------------------------
  // Stockists state accordion
  // ---------------------------------------------------------------
  document.querySelectorAll('.state-toggle').forEach((btn) => {
    btn.addEventListener('click', () => {
      const block = btn.closest('.state-block');
      block.classList.toggle('open');
    });
  });

  // ---------------------------------------------------------------
  // Reviews carousel buttons
  // ---------------------------------------------------------------
  document.querySelectorAll('[data-carousel]').forEach((carousel) => {
    const prevBtn = carousel.parentElement.querySelector('[data-carousel-prev]');
    const nextBtn = carousel.parentElement.querySelector('[data-carousel-next]');
    const step = () => {
      const card = carousel.querySelector('.review-card, .product-card, .bundle-card');
      return card ? card.getBoundingClientRect().width + 24 : 320;
    };
    if (prevBtn) prevBtn.addEventListener('click', () => carousel.scrollBy({ left: -step(), behavior: 'smooth' }));
    if (nextBtn) nextBtn.addEventListener('click', () => carousel.scrollBy({ left: step(), behavior: 'smooth' }));
  });

  // ---------------------------------------------------------------
  // Product gallery thumb-switching
  // ---------------------------------------------------------------
  document.querySelectorAll('.product-gallery').forEach((gallery) => {
    const main = gallery.querySelector('.main img');
    const thumbs = gallery.querySelectorAll('.thumb');
    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const src = thumb.querySelector('img')?.src;
        if (src && main) main.src = src.replace(/&width=\d+/, '&width=1200');
        thumbs.forEach(t => t.classList.remove('active'));
        thumb.classList.add('active');
      });
    });
  });

  // ---------------------------------------------------------------
  // Colour swatch selection
  // ---------------------------------------------------------------
  document.querySelectorAll('.swatch-row').forEach((row) => {
    const label = row.parentElement?.querySelector('[data-swatch-label]');
    row.querySelectorAll('.swatch').forEach((sw) => {
      sw.addEventListener('click', () => {
        row.querySelectorAll('.swatch').forEach(s => s.classList.remove('active'));
        sw.classList.add('active');
        if (label) label.textContent = sw.dataset.label || '';
        // Swap main image if this swatch references one
        const newSrc = sw.dataset.image;
        const main = document.querySelector('.product-gallery .main img');
        if (newSrc && main) main.src = newSrc;
      });
    });
  });

  // ---------------------------------------------------------------
  // Quantity stepper
  // ---------------------------------------------------------------
  document.querySelectorAll('.qty-stepper').forEach((stepper) => {
    const input = stepper.querySelector('input');
    const minus = stepper.querySelector('[data-qty-minus]');
    const plus = stepper.querySelector('[data-qty-plus]');
    if (minus) minus.addEventListener('click', () => {
      const v = parseInt(input.value || '1', 10);
      if (v > 1) input.value = v - 1;
    });
    if (plus) plus.addEventListener('click', () => {
      const v = parseInt(input.value || '1', 10);
      input.value = v + 1;
    });
  });

  // ---------------------------------------------------------------
  // Shop page: filter chips + view toggle (products ↔ find-your-fit)
  // ---------------------------------------------------------------
  const chips = document.querySelectorAll('[data-filter]');
  const productView = document.querySelector('[data-view="products"]');
  const fitView = document.querySelector('[data-view="find-your-fit"]');

  const setActiveFilter = (filterValue, opts = {}) => {
    if (!chips.length) return;
    const { scroll = false, updateHash = true, behavior = 'smooth' } = opts;

    // Active chip state
    chips.forEach(c => c.classList.toggle('active', c.dataset.filter === filterValue));

    if (filterValue === 'find-your-fit') {
      // Switch to find-your-fit view
      if (productView) productView.classList.remove('active');
      if (fitView) fitView.classList.add('active');
      if (updateHash) history.replaceState(null, '', '#find-your-fit');
      if (scroll && fitView) {
        requestAnimationFrame(() => {
          fitView.scrollIntoView({ behavior, block: 'start' });
        });
      }
    } else {
      // Switch to product grid view + filter cards
      if (fitView) fitView.classList.remove('active');
      if (productView) productView.classList.add('active');
      if (updateHash) history.replaceState(null, '', window.location.pathname);
      document.querySelectorAll('[data-cat]').forEach((card) => {
        const cats = (card.dataset.cat || '').split(/\s+/);
        card.style.display = (filterValue === 'all' || cats.includes(filterValue)) ? '' : 'none';
      });
    }

    // Re-trigger reveal on newly visible elements
    document.querySelectorAll('[data-view].active .reveal').forEach(el => {
      el.classList.add('in-view');
    });
  };

  if (chips.length) {
    chips.forEach((chip) => {
      chip.addEventListener('click', () => {
        setActiveFilter(chip.dataset.filter, { scroll: chip.dataset.filter === 'find-your-fit' });
      });
    });

    // Hero / nav "Find your fit" triggers
    document.querySelectorAll('[data-find-fit-trigger]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        setActiveFilter('find-your-fit', { scroll: true });
      });
    });

    // Init from URL hash on load.
    if (window.location.hash === '#find-your-fit') {
      setActiveFilter('find-your-fit', { scroll: true, updateHash: false, behavior: 'auto' });
    } else {
      // Default — make sure products view is active
      if (productView) productView.classList.add('active');
    }

    // Listen for hash changes (back/forward nav)
    window.addEventListener('hashchange', () => {
      if (window.location.hash === '#find-your-fit') {
        setActiveFilter('find-your-fit', { scroll: true, updateHash: false, behavior: 'auto' });
      } else {
        setActiveFilter('all', { updateHash: false });
      }
    });
  }

})();
